import type { IndexFiltersProps, TabProps } from '@shopify/polaris';
import {
  BlockStack,
  Button,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  InlineStack,
  Thumbnail,
  useBreakpoints,
  useIndexResourceState,
  useSetIndexFiltersMode,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { config } from '@/config';
import { ProductAvailabilityTable } from '@/constants/enum';
import { arrayToStringByComma } from '@/helpers';
import { useGetProductsAndCollectionsQuery } from '@/redux/query';
import BoldSmallText from '../BoldSmallText';
import RegularText from '../RegularText';
import IconNoImage from './no-image';

type ProductIndexTableProps = {
  productIds: string[];
  setProductIds: (productIds: string[]) => void;
  collectionIds: string[];
  setCollectionIds: (collectionIds: string[]) => void;
  disabledFeature: boolean;
};

type Product = {
  id: string;
  title: string;
  featuredMedia: {
    id: string;
    preview: {
      image: {
        altText: string;
        url: string;
        height: number;
        width: number;
      };
    };
  };
};

type Collection = {
  id: string;
  title: string;
  image: {
    altText: string;
    url: string;
    height: number;
    width: number;
  };
};

const ProductIndexTable = ({
  productIds,
  setProductIds,
  collectionIds,
  setCollectionIds,
  disabledFeature,
}: ProductIndexTableProps) => {
  const [currentProducts, setCurrentProducts] = useState<Product[]>([]);
  const [filterProducts, setFilterProducts] = useState<Product[]>([]);
  const [currentCollections, setCurrentCollections] = useState<Collection[]>([]);
  const [filterCollections, setFilterCollections] = useState<Collection[]>([]);
  const [selected, setSelected] = useState(0);
  const itemStrings = useMemo(() => ['Products', 'Collections'], []);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalItems: 0,
  });
  const [querySearchProduct, setQuerySearchProduct] = useState<string>('');
  const {
    data: productsAndCollectionsData,
    isFetching,
    isLoading,
  } = useGetProductsAndCollectionsQuery({
    shop: config.shop,
    productIds: arrayToStringByComma(productIds),
    collectionIds: arrayToStringByComma(collectionIds),
  });

  const resourceName = useMemo(() => {
    if (selected === ProductAvailabilityTable.PRODUCT) {
      return { singular: 'product', plural: 'products' };
    }

    return { singular: 'collection', plural: 'collections' };
  }, [selected]);

  const handleFiltersQueryChange = useCallback(
    (value: string) => {
      setQuerySearchProduct(value);

      if (!value.trim()) {
        setFilterProducts(currentProducts);
        setFilterCollections(currentCollections);
        return;
      }

      if (selected === 0) {
        setFilterProducts(currentProducts.filter(({ title }) => title.toLowerCase().includes(value.toLowerCase())));
      } else {
        setFilterCollections(currentCollections.filter(({ title }) => title.toLowerCase().includes(value.toLowerCase())));
      }
    },
    [currentCollections, currentProducts, selected],
  );

  useEffect(() => {
    if (!querySearchProduct.trim()) {
      setFilterProducts(currentProducts);
      setFilterCollections(currentCollections);
      return;
    }

    if (selected === 0) {
      setFilterProducts(currentProducts.filter(({ title }) => title.toLowerCase().includes(querySearchProduct.toLowerCase())));
    } else {
      setFilterCollections(
        currentCollections.filter(({ title }) => title.toLowerCase().includes(querySearchProduct.toLowerCase())),
      );
    }
  }, [currentCollections, currentProducts, querySearchProduct, selected]);

  useEffect(() => {
    if (productsAndCollectionsData?.data.products) {
      setCurrentProducts(productsAndCollectionsData?.data.products);
      setFilterProducts(productsAndCollectionsData?.data.products);
    }
    if (productsAndCollectionsData?.data.collections) {
      setCurrentCollections(productsAndCollectionsData?.data.collections);
      setFilterCollections(productsAndCollectionsData?.data.collections);
    }
  }, [productsAndCollectionsData?.data.collections, productsAndCollectionsData?.data.products]);

  const {
    selectedResources: productSelectedResources,
    // allResourcesSelected: productAllResourcesSelected,
    handleSelectionChange: productHandleSelectionChange,
    clearSelection: clearProductSelection,
  } = useIndexResourceState(filterProducts);

  const {
    selectedResources: collectionSelectedResources,
    // allResourcesSelected: collectionAllResourcesSelected,
    handleSelectionChange: collectionHandleSelectionChange,
    clearSelection: clearCollectionSelection,
  } = useIndexResourceState(filterCollections);

  const filters: IndexFiltersProps['filters'] = [];

  const tabs: TabProps[] = useMemo(
    () =>
      itemStrings.map((item, index) => ({
        content: item,
        index,
        badge: index === 0 ? currentProducts.length.toString() : currentCollections.length.toString(),
        onAction: () => { },
        id: `${item}-${index}`,
        isLocked: index === 0,
      })),
    [itemStrings, currentProducts.length, currentCollections.length],
  );

  const { mode, setMode } = useSetIndexFiltersMode();
  const onHandleCancel = () => {
    setMode(IndexFiltersMode.Default);
    handleFiltersQueryChange('');
  };

  const onSelectTab = (index: number) => {
    setSelected(index);
  };

  const handleFiltersClearAll = () => {
    handleFiltersQueryChange('');
  };

  const handleRemoveProduct = (id: string) => {
    const newProductIds = productIds.filter((productId) => !id.includes(productId.toString()));
    setProductIds(newProductIds);
  };

  const handleRemoveCollection = (id: string) => {
    const newCollectionIds = collectionIds.filter((collectionId) => !id.includes(collectionId.toString()));
    setCollectionIds(newCollectionIds);
  };

  const handleBulkDelete = () => {
    if (selected === 0) {
      const newProductIds = productIds.filter((id) => !productSelectedResources.map((gid) => gid.split('/').pop()).includes(id));
      setProductIds(newProductIds);
      clearProductSelection();
    } else {
      const newCollectionIds = collectionIds.filter(
        (id) => !collectionSelectedResources.map((gid) => gid.split('/').pop()).includes(id),
      );
      setCollectionIds(newCollectionIds);
      clearCollectionSelection();
    }
  };

  const promotedBulkActions = [
    {
      content: 'Delete',
      onAction: handleBulkDelete,
    },
  ];

  const currentPageProduct = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage;
    const end = start + pagination.perPage;

    return filterProducts.slice(start, end) ? filterProducts.slice(start, end) : filterProducts;
  }, [filterProducts, pagination]);

  const currentPageCollection = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage;
    const end = start + pagination.perPage;

    return filterCollections.slice(start, end) ? filterCollections.slice(start, end) : filterCollections;
  }, [filterCollections, pagination]);

  const productRowMarkup = currentPageProduct.map(({ id, title, featuredMedia }, index) => (
    <IndexTable.Row id={id} key={id} selected={productSelectedResources.includes(id)} position={index}>
      <IndexTable.Cell>
        <InlineStack blockAlign="center" gap="300" wrap={false}>
          <Thumbnail
            source={featuredMedia?.preview.image?.url ? featuredMedia?.preview.image?.url : IconNoImage}
            alt={featuredMedia?.preview.image?.altText || title}
            size="small"
          />
          <div style={{ width: '100%' }}>
            <RegularText truncate={true}>{title}</RegularText>
          </div>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack align="end">
          <Button onClick={() => handleRemoveProduct(id)} icon={DeleteIcon} tone="critical" disabled={disabledFeature}></Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const collectionRowMarkup = currentPageCollection.map(({ id, title, image }, index) => (
    <IndexTable.Row id={id} key={id} selected={collectionSelectedResources.includes(id)} position={index}>
      <IndexTable.Cell>
        <InlineStack blockAlign="center" gap="300">
          <Thumbnail source={image?.url ? image?.url : IconNoImage} alt={image?.altText ? image?.altText : title} size="small" />
          <BoldSmallText>{title}</BoldSmallText>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack align="end">
          <Button
            onClick={() => handleRemoveCollection(id)}
            icon={DeleteIcon}
            tone="critical"
            disabled={disabledFeature}
          ></Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  useEffect(() => {
    if (currentPageProduct.length === 0 && filterProducts.length > 0) {
      setPagination((prev) => ({
        ...prev,
        page: Math.ceil(filterProducts.length / prev.perPage),
      }));
    }
  }, [currentPageProduct, filterProducts.length]);

  useEffect(() => {
    if (currentPageCollection.length === 0 && filterCollections.length > 0) {
      setPagination((prev) => ({
        ...prev,
        page: Math.ceil(filterCollections.length / prev.perPage),
      }));
    }
  }, [currentCollections, currentPageCollection.length, filterCollections.length]);

  return (
    <BlockStack gap="200">
      <IndexFilters
        filters={filters}
        queryValue={querySearchProduct}
        queryPlaceholder={`Searching in ${resourceName.plural}`}
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={() => handleFiltersQueryChange('')}
        onClearAll={handleFiltersClearAll}
        cancelAction={{
          onAction: onHandleCancel,
          disabled: false,
          loading: false,
        }}
        mode={mode}
        setMode={setMode}
        tabs={tabs}
        selected={selected}
        onSelect={onSelectTab}
        canCreateNewView={false}
      />
      <IndexTable
        condensed={useBreakpoints().smDown}
        resourceName={resourceName}
        itemCount={selected === ProductAvailabilityTable.PRODUCT ? filterProducts.length : filterCollections.length}
        // selectable={false}
        selectedItemsCount={
          selected === ProductAvailabilityTable.PRODUCT ? productSelectedResources.length : collectionSelectedResources.length
        }
        onSelectionChange={
          selected === ProductAvailabilityTable.PRODUCT ? productHandleSelectionChange : collectionHandleSelectionChange
        }
        headings={[
          { title: `${resourceName.plural}`.charAt(0).toUpperCase() + `${resourceName.plural}`.slice(1) },
          { title: '' },
        ]}
        loading={isLoading || isFetching}
        emptyState={
          <RegularText>
            No {resourceName.plural} {querySearchProduct ? `matching "${querySearchProduct}"` : 'selected'}
          </RegularText>
        }
        pagination={
          filterProducts.length > pagination.perPage && selected === 0
            ? {
              hasNext: filterProducts.length > pagination.page * pagination.perPage,
              onNext: () => {
                setPagination((prev) => ({
                  ...prev,
                  page: prev.page + 1,
                }));
              },
              hasPrevious: pagination.page > 1,
              onPrevious: () => {
                setPagination((prev) => ({
                  ...prev,
                  page: prev.page - 1,
                }));
              },
              label: `${pagination.page} / ${Math.ceil(filterProducts.length / pagination.perPage)}`,
            }
            : filterCollections.length > pagination.perPage && selected === 1
              ? {
                hasNext: filterCollections.length > pagination.page * pagination.perPage,
                onNext: () => {
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }));
                },
                hasPrevious: pagination.page > 1,
                onPrevious: () => {
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page - 1,
                  }));
                },
                label: `${pagination.page} / ${Math.ceil(filterCollections.length / pagination.perPage)}`,
              }
              : undefined
        }
        promotedBulkActions={promotedBulkActions}
      >
        {selected === ProductAvailabilityTable.PRODUCT ? productRowMarkup : collectionRowMarkup}
      </IndexTable>
    </BlockStack>
  );
};

export default ProductIndexTable;
